import { useEffect, useRef, useState } from 'react';
import type { WeatherPlace } from '@frisk/shared';
import { api } from '../api';
import { t } from '../theme';

/**
 * Picking a town is the entire configuration.
 *
 * Open-Meteo needs no account and no API key, so there is nothing to
 * authenticate and no credential to store — which is why this is a location
 * picker rather than a "connect" flow like Bring's.
 */
export function WeatherConnect({ onClose, onChosen }: { onClose: () => void; onChosen: () => void }) {
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState<WeatherPlace[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const seq = useRef(0);

  useEffect(() => {
    input.current?.focus();
  }, []);

  // Debounced: this proxies a third party, and one request per keystroke would
  // be both rude and slower than typing.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setPlaces(null);
      setError(null);
      return;
    }
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await api.weatherSearch(q);
        // A slower earlier request must not overwrite a newer answer.
        if (mine !== seq.current) return;
        setPlaces(res.places);
        setError(res.places.length === 0 ? 'Kein Ort gefunden' : null);
      } catch (err) {
        if (mine !== seq.current) return;
        setError(err instanceof Error ? err.message : 'Ortssuche fehlgeschlagen');
        setPlaces(null);
      } finally {
        if (mine === seq.current) setBusy(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const choose = async (place: WeatherPlace) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.weatherSetPlace(place);
      onChosen();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ort konnte nicht gespeichert werden');
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'oklch(0.12 0.01 60 / 0.72)',
        backdropFilter: 'blur(8px)',
        animation: 'fade 160ms ease-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 35,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (places?.[0]) void choose(places[0]);
        }}
        style={{
          width: 620,
          maxWidth: '92vw',
          borderRadius: 30,
          background: t.surface,
          border: '1px solid oklch(1 0 0 / 0.09)',
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          animation: 'sheet 200ms ease-out',
        }}
      >
        <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>
          WETTER — ORT WÄHLEN
        </div>
        <div style={{ fontSize: 30, fontWeight: 500, letterSpacing: '-0.035em' }}>Welcher Ort?</div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>ORT</span>
          <input
            ref={input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="z. B. Kassel"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            style={{
              background: 'oklch(0.2 0.012 60)',
              border: '1px solid oklch(1 0 0 / 0.12)',
              borderRadius: 16,
              padding: '16px 18px',
              color: t.ink,
              caretColor: t.accent,
              fontSize: 22,
              fontFamily: t.sans,
              outline: 'none',
            }}
          />
        </label>

        {places && places.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
            {places.map((p) => (
              <div
                key={`${p.latitude},${p.longitude}`}
                className="press"
                onClick={() => void choose(p)}
                style={{
                  padding: '14px 18px',
                  borderRadius: 16,
                  background: t.surfaceSunken,
                  border: t.hairline,
                  minWidth: 0,
                }}
              >
                <div style={{ fontSize: 21, fontWeight: 500 }}>{p.name}</div>
                <div style={{ fontSize: 15, color: t.inkFaint, marginTop: 2 }}>{p.region}</div>
              </div>
            ))}
          </div>
        )}

        {error && <div style={{ fontSize: 16, color: 'oklch(0.82 0.14 30)', textWrap: 'pretty' }}>{error}</div>}

        <div style={{ fontSize: 15, color: t.inkFaint, textWrap: 'pretty' }}>
          Daten von Open-Meteo. Kein Konto, kein API-Schlüssel — frisk fragt vom Server aus ab,
          damit die Koordinaten das Haus nicht verlassen.
        </div>

        <div
          className="press"
          onClick={onClose}
          style={{
            height: 60,
            borderRadius: 16,
            border: t.hairlineStrong,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 19,
            fontWeight: 500,
            color: t.inkMuted,
          }}
        >
          Abbrechen
        </div>
      </form>
    </div>
  );
}
